import { PayloadAction, createAction, createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { Preferences } from '../../profile/objects/preferences';
import { Column } from '../../cms/objects/column';
import { LockableFareParameter, LockableFareParameterListCriteria } from '../objects/lockableFareParameter';
import { EMPTY_LOCKABLE_FARE_PARAMETER_LIST_CONFIGURATION, EMPTY_LOCKABLE_FARE_PARAMETER_LIST_CRITERIA } from '../constant/lockableFareParameterConstant';
import { setLockableFareParameterPreference } from '../utils/setLockableFareParameterPreference';

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

interface LockableFareParametersState {
    list: LockableFareParameter[];
    list_count: number;
    list_loading: boolean;
    list_criteria: LockableFareParameterListCriteria;
    list_configuration: { columns: Column[]; preferences: Preferences[] };
    item: LockableFareParameter | null;
    refresh: boolean;
    first_preference_loaded: boolean;
}

const initialState: LockableFareParametersState = {
    list: [],
    list_count: 0,
    list_loading: false,
    list_criteria: EMPTY_LOCKABLE_FARE_PARAMETER_LIST_CRITERIA,
    list_configuration: EMPTY_LOCKABLE_FARE_PARAMETER_LIST_CONFIGURATION,
    item: null,
    refresh: false,
    first_preference_loaded: false,
};

export const lockableFareParameters = createSlice({
    name: 'lockable_fare_parameters',
    initialState,
    reducers: {
        updateList(state, action: PayloadAction<LockableFareParameter[]>) {
            state.list = action.payload;
        },
        updateListCount(state, action: PayloadAction<number>) {
            state.list_count = action.payload;
        },
        updateListLoading(state, action: PayloadAction<boolean>) {
            state.list_loading = action.payload;
        },
        updateListCriteria(state, action: PayloadAction<LockableFareParameterListCriteria>) {
            state.list_criteria = action.payload;
        },
        toggleRefresh(state) {
            state.refresh = !state.refresh;
        },
        updateItem(state, action: PayloadAction<LockableFareParameter | null>) {
            state.item = action.payload;
        },
        addListConfigurationPreference(state, action: PayloadAction<Preferences[]>) {
            const preferences = cloneDeep(state.list_configuration.preferences);
            action.payload.forEach(p => preferences.push(p));
            state.list_configuration.preferences = preferences;
        },
        setListConfigurationPreference(state, action: PayloadAction<Preferences[]>) {
            state.list_configuration.preferences = action.payload;
        },
        setColumns(state, action: PayloadAction<Column[]>) {
            state.list_configuration.columns = action.payload;
        },
        toggleColumn(state, action: PayloadAction<number>) {
            const columns = cloneDeep(state.list_configuration.columns);
            columns[action.payload].checked = !columns[action.payload].checked;
            state.list_configuration.columns = columns;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setPreferences, (state, action) => {
            const result = setLockableFareParameterPreference(action.payload, state.list_configuration.columns);
            state.list_configuration.columns = result.columns;
            state.list_configuration.preferences = result.preferences;
            if (result.order_by) {
                state.list_criteria.order_by = result.order_by;
                state.list_criteria.order = result.order;
            }
            state.first_preference_loaded = true;
        });
    },
});

export const {
    updateList,
    updateListCount,
    updateListLoading,
    updateListCriteria,
    toggleRefresh,
    updateItem,
    addListConfigurationPreference,
    setListConfigurationPreference,
    setColumns,
    toggleColumn,
} = lockableFareParameters.actions;
